import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { ScrollReveal } from './ScrollReveal';
import { CalendarDays, BrainCircuit, Globe, Users, BookOpen, MessageCircle, Rocket } from 'lucide-react';
import { SectionCTA } from './SectionCTA';
const deliverables = [{
  num: '01',
  icon: <CalendarDays className="w-7 h-7" data-id="element-383" />,
  title: 'Método paso a paso de 15 días',
  desc: 'Una estructura clara de ejecución diaria para pasar de idea a proyecto digital. Cada día tiene una acción concreta para avanzar sin perder tiempo ni quedarte en teoría.',
  accent: 'from-cyan-500 to-blue-600',
  glow: 'cyan'
}, {
  num: '02',
  icon: <BrainCircuit className="w-7 h-7" data-id="element-384" />,
  title: 'Inteligencia Artificial para monetizar',
  desc: 'Aprenderás a utilizar la IA como herramienta estratégica para diseñar, estructurar y acelerar la creación de tu proyecto digital, optimizando procesos que tomarían meses.',
  accent: 'from-purple-500 to-violet-600',
  glow: 'purple'
}, {
  num: '03',
  icon: <Globe className="w-7 h-7" data-id="element-385" />,
  title: 'Tu página lista para vender al mundo',
  desc: 'Tendrás una página preparada para presentar tu oferta profesionalmente y recibir clientes desde cualquier parte del mundo, convirtiendo tu conocimiento en un activo digital real.',
  accent: 'from-emerald-500 to-green-600',
  glow: 'emerald'
}, {
  num: '04',
  icon: <Users className="w-7 h-7" data-id="element-386" />,
  title: 'Entrenamiento de mentalidad en comunidad',
  desc: 'Formarás parte de un entorno donde cientos de emprendedores están construyendo y ejecutando sus proyectos día a día. Esto fortalece tu enfoque, disciplina y capacidad de acción.',
  accent: 'from-amber-500 to-orange-600',
  glow: 'amber'
}, {
  num: '05',
  icon: <BookOpen className="w-7 h-7" data-id="element-387" />,
  title: 'Ebooks de marketing digital cada semana',
  desc: 'Material adicional sobre estrategias de marketing, ventas y posicionamiento digital para reforzar tu conocimiento y mejorar tu capacidad de monetización.',
  accent: 'from-rose-500 to-pink-600',
  glow: 'rose'
}, {
  num: '06',
  icon: <MessageCircle className="w-7 h-7" data-id="element-388" />,
  title: 'Comunidad enfocada en monetizar',
  desc: 'Acceso a una comunidad donde podrás compartir avances, resolver dudas y aprender de otros emprendedores que están aplicando el mismo sistema.',
  accent: 'from-sky-500 to-indigo-600',
  glow: 'sky'
}, {
  num: '07',
  icon: <Rocket className="w-7 h-7" data-id="element-389" />,
  title: 'Tu proyecto digital listo para vender',
  desc: 'Al finalizar tendrás tu propio proyecto digital estructurado, con una oferta clara y preparado para empezar a vender. Un activo real construido a partir de tu conocimiento.',
  accent: 'from-cyan-400 to-purple-600',
  glow: 'cyan'
}];
function DeliverableCard({
  item,
  index
}: {
  item: (typeof deliverables)[0];
  index: number;
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-40px'
  });
  return <motion.div ref={ref} initial={{
    opacity: 0,
    y: 60,
    scale: 0.95
  }} animate={isInView ? {
    opacity: 1,
    y: 0,
    scale: 1
  } : {}} transition={{
    duration: 0.6,
    delay: index * 0.1,
    ease: 'easeOut'
  }} className="group relative" data-id="element-390">
      {/* Card */}
      <div className="relative h-full rounded-2xl bg-[#0c0c20]/80 border border-white/[0.06] overflow-hidden transition-all duration-500 hover:border-white/[0.15] hover:shadow-[0_0_40px_rgba(0,212,255,0.08)]" data-id="element-391">
        {/* Top gradient bar */}
        <div className={`h-1 w-full bg-gradient-to-r ${item.accent} opacity-40 group-hover:opacity-100 transition-opacity duration-500`} data-id="element-392" />

        <div className="p-6 md:p-7" data-id="element-393">
          {/* Number + Icon row */}
          <div className="flex items-center justify-between mb-5" data-id="element-394">
            <span className={`text-5xl font-black bg-gradient-to-br ${item.accent} bg-clip-text text-transparent opacity-30 group-hover:opacity-60 transition-opacity duration-500 leading-none`} data-id="element-395">
              {item.num}
            </span>
            <div className={`p-3 rounded-xl bg-gradient-to-br ${item.accent} bg-opacity-10 text-white/70 group-hover:text-white transition-colors duration-300`} data-id="element-396">
              {item.icon}
            </div>
          </div>

          {/* Title */}
          <h3 className="text-lg font-bold text-white mb-3 leading-snug group-hover:text-white/95 transition-colors" data-id="element-397">
            {item.title}
          </h3>

          {/* Description */}
          <p className="text-sm text-gray-500 leading-relaxed group-hover:text-gray-400 transition-colors duration-300" data-id="element-398">
            {item.desc}
          </p>
        </div>

        {/* Hover glow effect */}
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" data-id="element-399">
          <div className={`absolute -bottom-20 -right-20 w-40 h-40 rounded-full blur-[80px] bg-gradient-to-br ${item.accent} opacity-20`} data-id="element-400" />
        </div>
      </div>
    </motion.div>;
}
export function WhatYouGet() {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, {
    once: true,
    margin: '-100px'
  });
  return <section className="py-24 px-4 bg-[#050510] relative overflow-hidden" data-id="element-401">
      {/* Background cinematics */}
      <div className="absolute inset-0 pointer-events-none" data-id="element-402">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-purple-900/8 rounded-full blur-[150px]" data-id="element-403" />
        <div className="absolute bottom-0 right-0 w-[500px] h-[300px] bg-cyan-900/8 rounded-full blur-[120px]" data-id="element-404" />
      </div>

      <div className="container mx-auto max-w-6xl relative z-10" ref={sectionRef} data-id="element-405">
        {/* Section Header */}
        <ScrollReveal className="text-center mb-6" data-id="element-406">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/5 border border-white/10 mb-6" data-id="element-407">
            <span className="text-xs font-semibold uppercase tracking-widest text-cyan-400" data-id="element-408">
              Acceso completo
            </span>
          </div>
          <h2 className="text-3xl md:text-5xl font-extrabold leading-tight mb-4" data-id="element-409">
            ¿Qué recibirás al entrar a <br className="hidden md:block" data-id="element-410" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400" data-id="element-411">
              Monetiza tu Contenido?
            </span>
          </h2>
        </ScrollReveal>

        <ScrollReveal delay={0.15} className="text-center mb-16" data-id="element-412">
          <p className="text-lg text-gray-500 max-w-2xl mx-auto" data-id="element-413">
            Todo lo que necesitas para crear, lanzar y vender tu primer producto
            digital — en un solo programa.
          </p>
        </ScrollReveal>

        {/* Netflix-style Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-id="element-414">
          {deliverables.slice(0, 6).map((item, index) => <DeliverableCard key={item.num} item={item} index={index} data-id="element-415" />)}
        </div>

        {/* Featured last card - full width */}
        <div className="mt-5" data-id="element-416">
          <motion.div initial={{
          opacity: 0,
          y: 50
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.7,
          delay: 0.7,
          ease: 'easeOut'
        }} className="group relative" data-id="element-417">
            <div className="relative rounded-2xl bg-gradient-to-br from-[#0c0c20] to-[#10102a] border border-white/[0.08] overflow-hidden transition-all duration-500 hover:border-cyan-500/20 hover:shadow-[0_0_60px_rgba(0,212,255,0.1)]" data-id="element-418">
              <div className="h-1.5 w-full bg-gradient-to-r from-cyan-400 via-purple-500 to-pink-500 opacity-60 group-hover:opacity-100 transition-opacity duration-500" data-id="element-419" />

              <div className="p-8 md:p-10 flex flex-col md:flex-row items-center gap-8" data-id="element-420">
                {/* Left: Icon + Number */}
                <div className="flex items-center gap-5 flex-shrink-0" data-id="element-421">
                  <span className="text-7xl font-black bg-gradient-to-br from-cyan-400 to-purple-600 bg-clip-text text-transparent opacity-40 group-hover:opacity-70 transition-opacity duration-500 leading-none" data-id="element-422">
                    07
                  </span>
                  <div className="p-4 rounded-2xl bg-gradient-to-br from-cyan-500/10 to-purple-500/10 border border-white/5" data-id="element-423">
                    <Rocket className="w-10 h-10 text-cyan-400" data-id="element-424" />
                  </div>
                </div>

                {/* Right: Content */}
                <div className="text-center md:text-left" data-id="element-425">
                  <h3 className="text-xl md:text-2xl font-bold text-white mb-3" data-id="element-426">
                    Tu proyecto digital listo para vender
                  </h3>
                  <p className="text-gray-400 leading-relaxed max-w-xl" data-id="element-427">
                    Al finalizar tendrás tu propio proyecto digital
                    estructurado, con una oferta clara y preparado para empezar
                    a vender.{' '}
                    <span className="text-cyan-400 font-semibold" data-id="element-428">
                      Un activo real construido a partir de tu conocimiento.
                    </span>
                  </p>
                </div>
              </div>

              {/* Glow */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" data-id="element-429">
                <div className="absolute -bottom-20 left-1/2 -translate-x-1/2 w-60 h-60 rounded-full blur-[100px] bg-gradient-to-br from-cyan-500 to-purple-600 opacity-15" data-id="element-430" />
              </div>
            </div>
          </motion.div>
        </div>

        <SectionCTA headline="Accede a todo esto hoy" buttonText="OBTENER ACCESO COMPLETO — $16.39" data-id="element-431" />
      </div>
    </section>;
}